export type ChatRouteTransitionAction = 'none' | 'push' | 'replace';

export function normalizeRoutePath(href: string | null | undefined) {
  if (!href) {
    return '/';
  }

  const withoutQuery = href.split(/[?#]/)[0] ?? '';
  const withoutGroups = withoutQuery.replace(/\/\([^)]+\)/g, '');
  const normalized = withoutGroups.replace(/\/+$/, '') || '/';
  return normalized;
}

export function isChatRoutePath(pathname: string | null | undefined) {
  const path = normalizeRoutePath(pathname);
  return path === '/chat' || path.startsWith('/chat/');
}

export function getActiveChatThreadId(pathname: string | null | undefined) {
  const path = normalizeRoutePath(pathname);
  const match = path.match(/^\/chat\/([^/]+)/);
  return match?.[1] ?? null;
}

export function getChatRouteTransition(
  currentPathname: string | null | undefined,
  targetHref: string | null | undefined,
  options?: { replaceFromAnyRoute?: boolean },
): ChatRouteTransitionAction {
  const currentPath = normalizeRoutePath(currentPathname);
  const targetPath = normalizeRoutePath(targetHref);

  if (currentPath === targetPath) {
    return 'none';
  }

  if (
    isChatRoutePath(targetPath) &&
    (options?.replaceFromAnyRoute || isChatRoutePath(currentPath))
  ) {
    return 'replace';
  }

  return 'push';
}

/** Management pages do not need a second conversation beside them. */
export function getWebWorkspaceMode(pathname: string | null | undefined) {
  const path = normalizeRoutePath(pathname);
  if (isChatRoutePath(path)) return 'chat-only';
  if (path === '/connectors' || path.startsWith('/connectors/'))
    return 'content-only';
  return 'split';
}

/**
 * The web workspace's classes. A chat route with a file open in the canvas
 * panel lays out like My CV and Jobs (user test fix, 2026-09-25): the split
 * mode, so the chat keeps the split column and the file takes the content
 * panel's place (`is-artifact`), or the whole row (`is-artifact-expanded`).
 */
export function getWebWorkspaceClassName(
  pathname: string | null | undefined,
  panel: { artifactId: string | null; expanded: boolean },
): string {
  const routeMode = getWebWorkspaceMode(pathname);
  const mode = routeMode === 'chat-only' && panel.artifactId ? 'split' : routeMode;
  const artifact = panel.artifactId ? ` is-artifact${panel.expanded ? ' is-artifact-expanded' : ''}` : '';
  return `tabbio-workspace${mode === 'split' ? '' : ` is-${mode}`}${artifact}`;
}
